// src/stores/useOrgStore.js
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { fetchWithAuth } from "../api/fetchWithAuth";
import { PERSIST_KEYS } from "../auth/persistKeys";

/**
 * App-wide org context.
 *
 * Holds:
 *   activeOrgId  — UUID or null
 *   activeOrg    — { id, name, role } or null
 *   role         — convenience mirror of activeOrg.role for selector use
 *   loaded       — has the initial /orgs/me fetch completed?
 *
 * Persists only activeOrgId. activeOrg + role are refetched on load so they
 * can never drift from the backend source of truth (user_orgs table).
 */
export const useOrgStore = create(
  persist(
    (set, get) => ({
      activeOrgId: null,
      activeOrg: null,
      role: null,
      tierCode: null,
      tierName: null,
      loaded: false,

      /**
       * Fetch /api/v1/orgs/me and populate the store.
       *
       * Called from main.jsx at app bootstrap and from hooks/actions that
       * need a fresh snapshot (e.g. after an admin changes their own role).
       *
       * Safe to call concurrently — the backend is a single SELECT and the
       * store write is a single set().
       */
      loadActiveOrg: async () => {
        try {
          const res = await fetchWithAuth("/api/v1/orgs/me");

          if (!res.ok) {
            // Still mark loaded=true so gated UI doesn't hang in a loading state.
            set({ loaded: true });
            return;
          }

          const org = await res.json(); // may be null for users with no membership

          set({
            activeOrgId: org?.id ?? null,
            activeOrg: org ?? null,
            role: org?.role ?? null,
            tierCode: org?.tier_code ?? null,
            tierName: org?.tier_display_name ?? null,
            loaded: true,
          });
        } catch (err) {
          console.error("[org-store] loadActiveOrg failed", err);
          set({ loaded: true });
        }
      },

      // Kept for backward compat with any existing callers that set the
      // active org directly (e.g. platform-admin impersonation flows).
      // Prefer loadActiveOrg() in new code.
      setActiveOrg: (org) => {
        set({
          activeOrgId: org?.id ?? null,
          activeOrg: org ?? null,
          role: org?.role ?? null,
          tierCode: org?.tier_code ?? null,
          tierName: org?.tier_display_name ?? null,

        });
      },

      clearOrg: () => {
        set({
          activeOrgId: null,
          activeOrg: null,
          role: null,
          tierCode: null,
          tierName: null,
          loaded: false,
        });
      },
    }),
    {
      name: PERSIST_KEYS.activeOrg,
      // Only persist the ID. Role + full object come from /orgs/me every load
      // so a stale persisted role can never gate UI incorrectly.
      partialize: (s) => ({ activeOrgId: s.activeOrgId }),
    }
  )
);
